import { createStore, createEffect, createEvent } from "effector"
import { useUnit } from "effector-react"
import { uniq } from "lodash"

import { ApiLinkEntity, ApiLinkRel, ApiServiceName, ErrorCode, ViewSettingsStorage } from "config/constants"
import AssetService, { IGetNotificationsParams } from "services/AssetService"
import CustomError from "models/CustomError"
import { Guid, IMultipleEntities } from "@/types"
import ApiService from "services/ApiService"
import Notification from "models/Notification"
import { NotificationAny } from "models/NotificationAny"
import { filtersStore } from "./filters"
import viewSettingsStore from "./viewSettings"
import { collectFilterProps } from "utils/filters"
import AssetArticle from "models/AssetArticle"
import { addImplicitProfitCenterFiltersIfNeeded, filterOutNotAllowedProfitCenterFilters } from "./profitCenters"
import ArticleService from "services/ArticleService"
import ErrorHandler from "utils/errorHandler"
import NotificationAssetsNotShipped from "models/NotificationAssetsNotShipped"
import NotificationAssetOutOfOrder from "models/NotificationAssetOutOfOrder"
import NotificationUnplannedAssetReleased from "models/NotificationUnplannedAssetReleased"
import NotificationUnplannedAssetStored from "models/NotificationUnplannedAssetStored"
import NotificationAssetDisposed from "models/NotificationAssetDisposed"
import NotificationAssetSplittingFailed from "models/NotificationAssetSplittingFailed"
import NotificationStoringIncomplete from "models/NotificationStoringIncomplete"
import NotificationCollectiveReferenceNumberNotUpdated from "models/NotificationCollectiveReferenceNumberNotUpdated"
import NotificationCollectiveReferenceNumberUpdated from "models/NotificationCollectiveReferenceNumberUpdated"
import NotificationReferenceNumbersUpdated from "models/NotificationReferenceNumbersUpdated"
import NotificationAssetSplittingSucceeded from "models/NotificationAssetSplittingSucceeded"
import NotificationAssetInspectionPerformed from "models/NotificationAssetInspectionPerformed"
import NotificationManualReleaseCompleted from "models/NotificationManualReleaseCompleted"
import NotificationManualStoreCompleted from "models/NotificationManualStoreCompleted"
import NotificationAssetMaintenanceOperationHoursOverdueSoon from "models/NotificationAssetMaintenanceOperationHoursOverdueSoon"
import NotificationAssetMaintenanceTimeOverdueSoon from "models/NotificationAssetMaintenanceTimeOverdueSoon"
import NotificationAssetReactivated from "models/NotificationAssetReactivated"
import NotificationReactivateAssetFailed from "models/NotificationReactivateAssetFailed"

// Stores
const $reservationNumberCache = createStore<{ [key: string]: string | undefined }>({})
const $shippingorderNumberCache = createStore<{ [key: string]: string | undefined }>({})
const $unreadNotificationCount = createStore<number>(0)
const $assetsInfoCache = createStore<{ [key: string]: AssetArticle | undefined }>({})

// Events
const setReservationNumberCache = createEvent<{ [key: string]: string | undefined }>()
const setShippingOrderNumberCache = createEvent<{ [key: string]: string | undefined }>()
const setAssetInfoCache = createEvent<{ [key: string]: AssetArticle | undefined }>()
const setUnreadNotificationCount = createEvent<number>()

// Effects
const fetchUnreadNotificationCount = createEffect<void, number, CustomError>(async () => {
    const filters = filtersStore.getState().stores.notifications
    const includeDone = viewSettingsStore.viewSettings.getState().stores[ViewSettingsStorage.notifications].showDoneNotifications
    const allowedFilters = filterOutNotAllowedProfitCenterFilters(filters)
    const resultFilters = addImplicitProfitCenterFiltersIfNeeded(allowedFilters)
    const filterProps = collectFilterProps(resultFilters)
    try {
        const resp = await AssetService.getNotifications({
            ...filterProps,
            includeDone,
            pageSize: 0,
        })
        return resp.pagination?.totalCount || 0
    } catch (e) {
        ErrorHandler.handleHttpError(e)
    }
})

const fetchNotifications = createEffect<IGetNotificationsParams, IMultipleEntities<NotificationAny>, CustomError>(async params => {
    try {
        if (!params.pageNumber || params.pageNumber === 1) {
            setAssetInfoCache({})
        }
        const resp = await AssetService.getNotifications(params)
        const notifications = await prepareNotifications(resp.list)
        return {
            ...resp,
            list: notifications,
        }
    } catch (e) {
        ErrorHandler.handleHttpError(e)
    }
})

const toggleNotificationRead = createEffect<Notification, void, CustomError>(async notification => {
    if (!notification.canUpdate || !notification.linkUpdateNotification) {
        throw new CustomError({
            code: ErrorCode.forbidden
        })
    }

    try {
        await ApiService.followLink({
            link: notification.linkUpdateNotification,
            data: {
                isRead: !notification.isRead,
            }
        })
        const unreadNotificationCount = $unreadNotificationCount.getState()
        if (notification.isRead) {
            setUnreadNotificationCount(unreadNotificationCount + 1)
        } else {
            setUnreadNotificationCount(unreadNotificationCount - 1)
        }
    } catch (e) {
        ErrorHandler.handleHttpError(e)
    }
})

const toggleNotificationDone = createEffect<Notification, void, CustomError>(async notification => {
    if (!notification.canUpdate || !notification.linkUpdateNotification) {
        throw new CustomError({
            code: ErrorCode.forbidden
        })
    }

    try {
        const markAsDone = !notification.isDone
        const markAsRead = markAsDone && !notification.isRead ? true : undefined
        await ApiService.followLink({
            link: notification.linkUpdateNotification,
            data: {
                isDone: markAsDone,
                isRead: markAsRead,
            }
        })
        const unreadNotificationCount = $unreadNotificationCount.getState()
        if (markAsRead) {
            setUnreadNotificationCount(unreadNotificationCount - 1)
        }
    } catch (e) {
        ErrorHandler.handleHttpError(e)
    }
})

const markAllNotificationsAsRead = createEffect<IGetNotificationsParams, void, CustomError>(async params => {
    try {
        await ApiService.request({
            serviceName: ApiServiceName.asset,
            followLink: {
                entity: ApiLinkEntity.notification,
                rel: ApiLinkRel.delete,
            },
            data: params,
        })
    } catch (e) {
        ErrorHandler.handleHttpError(e)
    }
})


// Listeners
$unreadNotificationCount
    .on(fetchUnreadNotificationCount.doneData, (_, payload) => payload)
    .on(setUnreadNotificationCount, (_, payload) => payload)
    .on(markAllNotificationsAsRead.doneData, () => 0)

$assetsInfoCache
    .on(setAssetInfoCache, (_, payload) => payload)

// Helpers

async function prepareNotifications(notifications: NotificationAny[]) {
    const reservationIds = collectReservationIds(notifications)
    const reservationNumbers = await getReservationNumbers(reservationIds)

    const shippingOrderIds = collectShippingOrderIds(notifications)
    const shippingOrderNumbers = await getShippingOrderNumbers(shippingOrderIds)

    const assetIds = collectAssetIds(notifications)
    const assetsInfo = await getAssetsInfo(assetIds)
    return fillAdditionalNotificationInfo(notifications, assetsInfo, reservationNumbers, shippingOrderNumbers)
}

function collectReservationIds(notifications: NotificationAny[]) {
    let reservationIds: Guid[] = [];

    for (const notification of notifications) {
        if ('reservationId' in notification && notification.reservationId) {
            reservationIds.push(notification.reservationId);
        }

        if (notification instanceof NotificationUnplannedAssetReleased && notification.plannedFor && notification.plannedFor.length > 0) {
            reservationIds.push(notification.plannedFor[0])
        }
    }

    return uniq(reservationIds);
}

function getReservationNumbers(reservationIds: Guid[]) {
    const reservationNumberCache = $reservationNumberCache.getState()
    const cachedReservationIds = Object.keys(reservationNumberCache)
    const notCachedReservationIds = reservationIds.filter(reservationId => !cachedReservationIds.includes(reservationId))

    if (notCachedReservationIds.length === 0) {
        return reservationNumberCache
    }

    return AssetService.getReservations({ reservationIds, pageSize: reservationIds.length }).then((resp) => {
        const newReservationNumbers: { [x: string]: string | undefined } = {}
        for (const reservation of resp.list) {
            newReservationNumbers[reservation.id] = reservation.number
        }
        const newReservationNumberCache = {
            ...reservationNumberCache,
            ...newReservationNumbers,
        }

        setReservationNumberCache(newReservationNumberCache)
        return newReservationNumberCache
    })
}


function collectShippingOrderIds(notifications: NotificationAny[]) {
    let shippingOrderIds: Guid[] = [];

    for (const notification of notifications) {
        if ('shippingOrderId' in notification && notification.shippingOrderId) {
            shippingOrderIds.push(notification.shippingOrderId);
        }
    }

    return uniq(shippingOrderIds);
}

function getShippingOrderNumbers(shippingOrderIds: Guid[]) {
    const shippingOrderNumberCache = $shippingorderNumberCache.getState()
    const cachedShippingOrderIds = Object.keys(shippingOrderNumberCache)
    const notCachedShippingOrderIds = shippingOrderIds.filter(orderId => !cachedShippingOrderIds.includes(orderId))

    if (notCachedShippingOrderIds.length === 0) {
        return shippingOrderNumberCache
    }

    console.log("shippingOrderIds", shippingOrderIds)

    return AssetService.getShippingOrderOverview({ shippingOrderIds: notCachedShippingOrderIds, includeCompleted: true, pageSize: shippingOrderIds.length })
        .then((resp) => {
            const newShippingOrderNumber: { [x: string]: string | undefined } = {}
            for (const shippingOrderNumber of resp.list) {
                newShippingOrderNumber[shippingOrderNumber.id] = shippingOrderNumber.number
            }
            const newShippingOrderNumberCache = {
                ...shippingOrderNumberCache,
                ...newShippingOrderNumber,
            }

            console.log("newShippingOrderNumberCache", newShippingOrderNumberCache)

            setShippingOrderNumberCache(newShippingOrderNumberCache)
            return newShippingOrderNumberCache
        })
}

function collectAssetIds(notifications: NotificationAny[]) {
    let assetIds: Guid[] = []
    for (const notification of notifications) {
        if (notification instanceof NotificationAssetsNotShipped) {
            for (const transport of notification.transports) {
                assetIds = [...assetIds, ...transport.assetIds]
            }
        } else if (notification instanceof NotificationAssetOutOfOrder
            || notification instanceof NotificationUnplannedAssetReleased
            || notification instanceof NotificationUnplannedAssetStored
            || notification instanceof NotificationAssetDisposed
            || notification instanceof NotificationAssetSplittingFailed
            || notification instanceof NotificationAssetInspectionPerformed
            || notification instanceof NotificationAssetMaintenanceTimeOverdueSoon
            || notification instanceof NotificationAssetMaintenanceOperationHoursOverdueSoon
            || notification instanceof NotificationReactivateAssetFailed
            || notification instanceof NotificationAssetReactivated) {
            assetIds.push(notification.asset.assetId)
        }
        if (notification instanceof NotificationUnplannedAssetReleased
            && notification.replacedAsset) {
            assetIds.push(notification.replacedAsset.assetId)
        } else if (notification instanceof NotificationStoringIncomplete
            || notification instanceof NotificationCollectiveReferenceNumberNotUpdated) {
            assetIds = [...assetIds, ...notification.assetIds]
        } else if (notification instanceof NotificationCollectiveReferenceNumberUpdated) {
            assetIds = [...assetIds, ...notification.assetIds, notification.machineAssetId]
        } else if (notification instanceof NotificationReferenceNumbersUpdated) {
            assetIds = [...assetIds, ...notification.assetIds, notification.changedAssetId]
        } else if (notification instanceof NotificationAssetSplittingSucceeded) {
            const childrenIds = notification.children.map((item) => item.assetId)
            assetIds = [...assetIds, notification.parent.assetId, ...childrenIds]
        } else if (notification instanceof NotificationManualReleaseCompleted
            || notification instanceof NotificationManualStoreCompleted) {
            assetIds = [...assetIds, ...notification.inspectionRequiredForIds]
        }
    }
    return uniq(assetIds).filter(id => id != null)
}


async function getAssetsInfo(assetIds: Guid[]): Promise<{ [x: string]: AssetArticle | undefined }> {
    const assetsInfoCache = $assetsInfoCache.getState()
    const cachedAssetIds = Object.keys(assetsInfoCache)
    const notCachedAssetIds = assetIds.filter(assetId => !cachedAssetIds.includes(assetId))

    if (notCachedAssetIds.length === 0) {
        return assetsInfoCache
    }

    const resp = await ArticleService.getAssetArticles({
        assetIds: notCachedAssetIds,
        pageSize: notCachedAssetIds.length
    })
    const newAssetsInfo: { [x: string]: AssetArticle } = {}
    for (const assetArticle of resp.list) {
        newAssetsInfo[assetArticle.assetId] = assetArticle
    }
    const newAssetInfoCache = {
        ...assetsInfoCache,
        ...newAssetsInfo,
    }
    setAssetInfoCache(newAssetInfoCache)
    return newAssetInfoCache
}

async function fillAdditionalNotificationInfo(
    notifications: NotificationAny[],
    assetsInfo: { [x: string]: AssetArticle | undefined, },
    reservationNumbers: { [key: string]: string | undefined },
    shippingOrderNumbers: { [key: string]: string | undefined }) {
    return notifications.map(notification => {
        if (notification instanceof NotificationAssetsNotShipped) {
            notification.reservationNumber = reservationNumbers[notification.reservationId]
            notification.transports = notification.transports.map(transport => {
                transport.assets = transport.assets.map(asset => {
                    const assetInfo = assetsInfo[asset.assetId]
                    asset.lmid = assetInfo?.lmid
                    asset.isAssetLabeled = assetInfo?.isLabeled
                    asset.linkedToAssetCollection = assetInfo?.linkedToAssetCollection
                    asset.articleDescription = assetInfo?.altArticleDescription || assetInfo?.articleDescription
                    return asset
                })

                return transport
            })
        } else if (notification instanceof NotificationAssetOutOfOrder
            || notification instanceof NotificationUnplannedAssetReleased
            || notification instanceof NotificationUnplannedAssetStored
            || notification instanceof NotificationAssetDisposed
            || notification instanceof NotificationAssetSplittingFailed
            || notification instanceof NotificationAssetInspectionPerformed
            || notification instanceof NotificationAssetMaintenanceTimeOverdueSoon
            || notification instanceof NotificationAssetMaintenanceOperationHoursOverdueSoon) {
            const assetInfo = assetsInfo[notification.asset.assetId]
            notification.asset.lmid = assetInfo?.lmid
            notification.asset.isAssetLabeled = assetInfo?.isLabeled
            notification.asset.linkedToAssetCollection = assetInfo?.linkedToAssetCollection
            notification.asset.articleDescription = assetInfo?.altArticleDescription || assetInfo?.articleDescription
        } else if (notification instanceof NotificationReactivateAssetFailed
            || notification instanceof NotificationAssetReactivated) {
            const assetInfo = assetsInfo[notification.asset.assetId]
            notification.asset.lmid = assetInfo?.lmid || notification.asset.lmid
            notification.asset.isAssetLabeled = assetInfo?.isLabeled
            notification.asset.linkedToAssetCollection = assetInfo?.linkedToAssetCollection
            notification.asset.articleDescription = assetInfo?.altArticleDescription || assetInfo?.articleDescription || notification.asset.articleDescription
        } else if (notification instanceof NotificationStoringIncomplete
            || notification instanceof NotificationCollectiveReferenceNumberUpdated
            || notification instanceof NotificationReferenceNumbersUpdated
            || notification instanceof NotificationCollectiveReferenceNumberNotUpdated) {
            notification.assets = notification.assets.map(asset => {
                const assetInfo = assetsInfo[asset.assetId]
                asset.lmid = assetInfo?.lmid
                asset.isAssetLabeled = assetInfo?.isLabeled
                asset.linkedToAssetCollection = assetInfo?.linkedToAssetCollection
                asset.articleDescription = assetInfo?.altArticleDescription || assetInfo?.articleDescription
                return asset
            })
        } else if (notification instanceof NotificationManualReleaseCompleted
            || notification instanceof NotificationManualStoreCompleted) {
            notification.inspectionRequiredFor = notification.inspectionRequiredFor.map(asset => {
                const assetInfo = assetsInfo[asset.assetId]
                asset.lmid = assetInfo?.lmid
                asset.isAssetLabeled = assetInfo?.isLabeled
                asset.linkedToAssetCollection = assetInfo?.linkedToAssetCollection
                asset.articleDescription = assetInfo?.altArticleDescription || assetInfo?.articleDescription
                return asset
            })
        }

        if (notification instanceof NotificationUnplannedAssetReleased
            && notification.replacedAsset) {
            const assetInfo = assetsInfo[notification.replacedAsset.assetId]
            notification.replacedAsset.lmid = assetInfo?.lmid
            notification.replacedAsset.isAssetLabeled = assetInfo?.isLabeled
            notification.replacedAsset.linkedToAssetCollection = assetInfo?.linkedToAssetCollection
            notification.replacedAsset.articleDescription = assetInfo?.altArticleDescription || assetInfo?.articleDescription
        }
        if (notification instanceof NotificationAssetSplittingSucceeded && notification.parent.asset) {
            const parentAssetInfo = assetsInfo[notification.parent.assetId]
            notification.parent.asset.lmid = parentAssetInfo?.lmid
            notification.parent.asset.isAssetLabeled = parentAssetInfo?.isLabeled
            notification.parent.asset.linkedToAssetCollection = parentAssetInfo?.linkedToAssetCollection
            notification.parent.asset.articleDescription = parentAssetInfo?.altArticleDescription || parentAssetInfo?.articleDescription

            notification.children.forEach((child) => {
                if (!child.asset) return
                const childAssetInfo = assetsInfo[child.assetId]
                child.asset.lmid = childAssetInfo?.lmid
                child.asset.isAssetLabeled = childAssetInfo?.isLabeled
                child.asset.linkedToAssetCollection = childAssetInfo?.linkedToAssetCollection
                child.asset.articleDescription = childAssetInfo?.altArticleDescription || childAssetInfo?.articleDescription
            })
        }
        if (notification instanceof NotificationCollectiveReferenceNumberUpdated) {
            const assetInfo = assetsInfo[notification.machineAssetId]
            notification.machineAsset.lmid = assetInfo?.lmid
            notification.machineAsset.isAssetLabeled = assetInfo?.isLabeled
            notification.machineAsset.linkedToAssetCollection = assetInfo?.linkedToAssetCollection
            notification.machineAsset.articleDescription = assetInfo?.altArticleDescription || assetInfo?.articleDescription
        }

        if (notification instanceof NotificationReferenceNumbersUpdated) {
            const assetInfo = assetsInfo[notification.changedAssetId]
            notification.changedAsset.lmid = assetInfo?.lmid
            notification.changedAsset.isAssetLabeled = assetInfo?.isLabeled
            notification.changedAsset.linkedToAssetCollection = assetInfo?.linkedToAssetCollection
            notification.changedAsset.articleDescription = assetInfo?.altArticleDescription || assetInfo?.articleDescription
        }

        if (notification instanceof NotificationUnplannedAssetReleased && notification.plannedFor && notification.plannedFor.length > 0) {
            notification.anotherReservationNumber = reservationNumbers[notification.plannedFor[0]]
        }

        if ('reservationNumber' in notification && notification.reservationId) {
            notification.reservationNumber = reservationNumbers[notification.reservationId]
        }

        if ('shippingOrderId' in notification && notification.shippingOrderId) {
            notification.shippingOrderNumber = shippingOrderNumbers[notification.shippingOrderId]
        }

        return notification
    })
}


// Exports
export function useNotificationsStore() {
    return {
        unreadNotificationCount: useUnit($unreadNotificationCount),
        fetchNotifications,
        fetchUnreadNotificationCount,
        toggleNotificationRead,
        markAllNotificationsAsRead,
        toggleNotificationDone,
    }
}
